"use client";

import { Bar, BarChart, CartesianGrid, XAxis } from "recharts";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { RevenueByPlanData } from "@/api/revenue/get-revenue-by-plan";

const chartConfig = {
    "Total Income": {
        label: "Total Income",
        color: "#8b5cf6", // Modern purple
    },
} satisfies ChartConfig;

interface RevenueByPlanChartProps {
    data: RevenueByPlanData[];
}

export function RevenueByPlanChart({ data }: RevenueByPlanChartProps) {
    // Format the data for the chart
    const chartData = data.map(item => ({
        name: item.plan_name,
        "Total Income": item.total_income
    }));

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    return (
        <Card className="@container/card">
            <CardHeader>
                <CardTitle>Revenue by Plan</CardTitle>
                <CardDescription>
                    <span className="hidden @[540px]/card:block">Revenue distribution across different plans</span>
                    <span className="@[540px]/card:hidden">Plan revenue overview</span>
                </CardDescription>
            </CardHeader>
            <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
                <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
                    <BarChart
                        accessibilityLayer
                        data={chartData}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 60,
                        }}
                    >
                        <defs>
                            <linearGradient id="fillTotalIncome" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} />
                        <XAxis 
                            dataKey="name" 
                            angle={-45} 
                            textAnchor="end" 
                            height={60}
                            tick={{ fontSize: 12 }}
                            tickLine={false}
                            axisLine={false}
                        />
                        <ChartTooltip 
                            cursor={false}
                            content={
                                <ChartTooltipContent 
                                    formatter={(value) => [formatCurrency(Number(value)), "Total Income"]}
                                    labelFormatter={(label) => `Plan: ${label}`}
                                />
                            }
                        />
                        <Bar dataKey="Total Income" fill="url(#fillTotalIncome)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ChartContainer>
            </CardContent>
        </Card>
    );
}